import React, { useState } from 'react';
import { FiCheckCircle, FiLock } from 'react-icons/fi';
import { storeCarrierLogin, type CredentialStatus } from '../../services/automationService';

/**
 * Where an agency stores a carrier's login for "log in from the chat".
 *
 * What is typed here goes only to the automation server on this computer,
 * which writes it to automation/.env.local. It is not sent to the cloud,
 * not kept in the browser, and never shown again — only the user ID is
 * displayed afterwards so the person can tell which login is stored.
 */
const CarrierLoginStore: React.FC<{ carrierId: string; carrierName: string; status?: CredentialStatus; onChanged: () => void }> = ({ carrierId, carrierName, status, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Carriers without a login recipe cannot be logged in from the chat at all.
  if (!status || status.loginStored === undefined) return null;

  const save = async (remove = false) => {
    setBusy(true);
    setError('');
    try {
      const result = await storeCarrierLogin(carrierId, remove ? null : { username, password });
      if (!result.ok) { setError(result.message || 'That could not be saved.'); return; }
      setUsername('');
      setPassword('');
      setOpen(false);
      onChanged();
    } catch {
      setError('The automation server on this computer is not running.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ca-action">
      <div className="ca-action__row">
        <div className="ca-action__text">
          <div className="ca-action__label"><FiLock size={12} /> Login for the chat</div>
          <div className="ca-action__description">
            {status.loginStored
              ? <><FiCheckCircle size={11} /> Stored on this computer as <strong>{status.loginUsername}</strong>. The Digital Workforce can log in to {carrierName} with it and will ask you for any one-time code on a card.</>
              : <>No {carrierName} login is stored on this computer, so the Digital Workforce cannot log in for you. Add it here; it stays on this computer.</>}
          </div>
        </div>
        <button type="button" className="ca-run__btn" onClick={() => setOpen((v) => !v)}>{status.loginStored ? 'Change' : 'Add login'}</button>
      </div>
      {open && (
        <div className="ca-run__grid" style={{ marginTop: 10 }}>
          <label className="ca-run__field">
            <span>{carrierName} user ID</span>
            <input className="ca-run__input" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="off" />
          </label>
          <label className="ca-run__field">
            <span>{carrierName} password</span>
            <input className="ca-run__input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
          </label>
          <button type="button" className="ca-run__btn" disabled={busy || !username.trim() || !password} onClick={() => save()}>Save on this computer</button>
          {status.loginStored && <button type="button" className="ca-detail__disconnect" disabled={busy} onClick={() => save(true)}>Remove stored login</button>}
          {error && <div className="ca-action__description">{error}</div>}
        </div>
      )}
    </div>
  );
};

export default CarrierLoginStore;
